import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import api from '../../services/api';
import { toast } from 'sonner';
import { Users, User, UserX, RefreshCw } from 'lucide-react';

import { ManualMergeCard } from './duplicates/ManualMergeCard';
import { IntegrityWorkflowCard } from './duplicates/IntegrityWorkflowCard';
import { PlayerDuplicatesTab } from './duplicates/PlayerDuplicatesTab';
import { ParentDuplicatesTab } from './duplicates/ParentDuplicatesTab';
import { OrphanPlayersTab } from './duplicates/OrphanPlayersTab';
import { MergeComparisonDialog } from './duplicates/MergeComparisonDialog';
import { AddParentDialog } from './duplicates/AddParentDialog';

const DuplicateManagement = () => {
  const [loading, setLoading] = useState(true);
  const [playerDuplicates, setPlayerDuplicates] = useState([]);
  const [parentDuplicates, setParentDuplicates] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [activeTab, setActiveTab] = useState('players');

  // Merge dialog state
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeType, setMergeType] = useState(''); // 'player' or 'parent'
  const [comparisonData, setComparisonData] = useState(null);
  const [fieldSelections, setFieldSelections] = useState({});
  const [merging, setMerging] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState('');

  // Manual merge state
  const [manualMergeType, setManualMergeType] = useState('player');
  const [manualId1, setManualId1] = useState('');
  const [manualId2, setManualId2] = useState('');
  const [manualSearching, setManualSearching] = useState(false);

  // Add parent dialog
  const [addParentDialog, setAddParentDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [parentSearch, setParentSearch] = useState('');
  const [allParents, setAllParents] = useState([]);

  // Ignore state
  const [ignoring, setIgnoring] = useState({});

  // Tier-aware summary stats from the players endpoint.
  const [playerSummary, setPlayerSummary] = useState(null);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const [playersRes, parentsRes, orphansRes] = await Promise.all([
        api.get('/admin/duplicates/players'),
        api.get('/admin/duplicates/parents'),
        api.get('/admin/duplicates/orphan-players'),
      ]);
      setPlayerDuplicates(playersRes.data.duplicates || []);
      setPlayerSummary({
        exact_count: playersRes.data.exact_count ?? 0,
        strong_count: playersRes.data.strong_count ?? 0,
        possible_count: playersRes.data.possible_count ?? 0,
      });
      setParentDuplicates(parentsRes.data.duplicates || []);
      setOrphans(orphansRes.data.orphans || []);
    } catch (error) {
      toast.error('Failed to fetch duplicates');
    } finally {
      setLoading(false);
    }
  };

  const fetchParents = async () => {
    try {
      const response = await api.get('/parents');
      setAllParents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch parents:', error);
    }
  };

  useEffect(() => {
    fetchDuplicates();
    fetchParents();
  }, []);

  const openMergeDialog = async (type, id1, id2) => {
    setMergeType(type);
    setMerging(true);

    try {
      const endpoint = type === 'player'
        ? `/admin/duplicates/players/${id1}/${id2}/compare`
        : `/admin/duplicates/parents/${id1}/${id2}/compare`;

      const response = await api.get(endpoint);
      const data = response.data;

      const key1 = type === 'player' ? 'player1' : 'parent1';
      const key2 = type === 'player' ? 'player2' : 'parent2';

      if (!data || !data[key1] || !data[key2]) {
        toast.error('One or both records could not be loaded. The records may have been deleted or have invalid data.');
        return;
      }

      if (!data[key1].id || !data[key2].id) {
        toast.error('Records are missing required ID fields. Please contact support.');
        return;
      }

      setComparisonData(data);
      setSelectedTarget(data[key1].id);

      const initialSelections = {};
      (data.comparison || []).forEach(field => {
        initialSelections[field.field] = 'target';
      });
      setFieldSelections(initialSelections);

      setMergeDialog(true);
    } catch (error) {
      console.error('Merge dialog error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load comparison data. Please try refreshing the page.');
    } finally {
      setMerging(false);
    }
  };

  const handleSelectAll = (source) => {
    if (!comparisonData || !comparisonData.comparison) return;
    const newSelections = {};
    comparisonData.comparison.forEach(field => {
      newSelections[field.field] = source;
    });
    setFieldSelections(newSelections);
  };

  const handleMerge = async () => {
    if (!comparisonData) return;

    setMerging(true);
    try {
      const key1 = mergeType === 'player' ? 'player1' : 'parent1';
      const key2 = mergeType === 'player' ? 'player2' : 'parent2';

      const sourceId = selectedTarget === comparisonData[key1].id
        ? comparisonData[key2].id
        : comparisonData[key1].id;
      const targetId = selectedTarget;

      const adjustedSelections = {};
      Object.entries(fieldSelections).forEach(([field, selection]) => {
        adjustedSelections[field] = selection;
      });

      const endpoint = mergeType === 'player'
        ? '/admin/duplicates/players/merge'
        : '/admin/duplicates/parents/merge';

      await api.post(endpoint, {
        source_id: sourceId,
        target_id: targetId,
        field_selections: adjustedSelections,
      });

      toast.success('Records merged successfully');
      setMergeDialog(false);
      fetchDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge records');
    } finally {
      setMerging(false);
    }
  };

  const handleAddParent = async (parentId) => {
    if (!selectedPlayer) return;

    try {
      await api.post('/admin/duplicates/players/add-parent', {
        player_id: selectedPlayer.id,
        parent_id: parentId,
      });

      toast.success('Parent added to player');
      setAddParentDialog(false);
      setSelectedPlayer(null);
      fetchDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add parent');
    }
  };

  const handleIgnore = async (id1, id2, entityType) => {
    const pairKey = `${id1}-${id2}`;
    setIgnoring(prev => ({ ...prev, [pairKey]: true }));

    try {
      await api.post('/admin/duplicates/ignore', {
        id1,
        id2,
        entity_type: entityType,
      });

      toast.success('Pair ignored - will not appear in duplicates');

      if (entityType === 'player') {
        setPlayerDuplicates(prev =>
          prev.filter(d => !(d.player1.id === id1 && d.player2.id === id2) &&
                          !(d.player1.id === id2 && d.player2.id === id1))
        );
      } else {
        setParentDuplicates(prev =>
          prev.filter(d => !(d.parent1.id === id1 && d.parent2.id === id2) &&
                          !(d.parent1.id === id2 && d.parent2.id === id1))
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ignore pair');
    } finally {
      setIgnoring(prev => ({ ...prev, [pairKey]: false }));
    }
  };

  const handleManualMerge = async () => {
    if (!manualId1.trim() || !manualId2.trim()) {
      toast.error('Please enter both IDs');
      return;
    }

    if (manualId1.trim() === manualId2.trim()) {
      toast.error('Please enter two different IDs');
      return;
    }

    setManualSearching(true);
    try {
      const endpoint = manualMergeType === 'player'
        ? `/admin/duplicates/players/lookup-compare`
        : `/admin/duplicates/parents/lookup-compare`;

      const response = await api.post(endpoint, {
        id1: manualId1.trim(),
        id2: manualId2.trim(),
      });

      setMergeType(manualMergeType);
      setComparisonData(response.data);

      const key1 = manualMergeType === 'player' ? 'player1' : 'parent1';
      setSelectedTarget(response.data[key1].id);

      const initialSelections = {};
      response.data.comparison.forEach(field => {
        initialSelections[field.field] = 'target';
      });
      setFieldSelections(initialSelections);

      setMergeDialog(true);
      setManualId1('');
      setManualId2('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to find records. Please check the IDs and try again.';
      toast.error(message);
    } finally {
      setManualSearching(false);
    }
  };

  const handleLinkParent = (orphan) => {
    setSelectedPlayer({ id: orphan.id, full_name: orphan.name });
    setAddParentDialog(true);
    setParentSearch('');
  };

  return (
    <DashboardLayout title="Merge Accounts">
      <div className="space-y-6" data-testid="duplicate-management-page">
        <div className="space-y-3">
          <div>
            <p className="text-slate-600">Detect and merge duplicate records</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchDuplicates}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          </div>
        </div>

        <IntegrityWorkflowCard onCompleted={fetchDuplicates} />

        <ManualMergeCard
          manualMergeType={manualMergeType}
          setManualMergeType={setManualMergeType}
          manualId1={manualId1}
          setManualId1={setManualId1}
          manualId2={manualId2}
          setManualId2={setManualId2}
          manualSearching={manualSearching}
          onCompare={handleManualMerge}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="players" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Players
              {playerDuplicates.length > 0 && (
                <Badge variant="destructive" className="ml-1">{playerDuplicates.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="parents" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Parents & Customers
              {parentDuplicates.length > 0 && (
                <Badge variant="destructive" className="ml-1">{parentDuplicates.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="orphans" className="flex items-center gap-2" data-testid="tab-orphans">
              <UserX className="h-4 w-4" />
              Orphan Players
              {orphans.length > 0 && (
                <Badge variant="destructive" className="ml-1">{orphans.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="players" className="space-y-4 mt-4">
            <PlayerDuplicatesTab
              loading={loading}
              duplicates={playerDuplicates}
              summary={playerSummary}
              onMerge={openMergeDialog}
              onIgnore={handleIgnore}
              ignoring={ignoring}
            />
          </TabsContent>

          <TabsContent value="parents" className="space-y-4 mt-4">
            <ParentDuplicatesTab
              loading={loading}
              duplicates={parentDuplicates}
              onMerge={openMergeDialog}
              onIgnore={handleIgnore}
              ignoring={ignoring}
              onAutoMergeApplied={fetchDuplicates}
            />
          </TabsContent>

          <TabsContent value="orphans" className="space-y-4 mt-4">
            <OrphanPlayersTab
              loading={loading}
              orphans={orphans}
              onLinkParent={handleLinkParent}
              onRefresh={fetchDuplicates}
            />
          </TabsContent>
        </Tabs>

        <MergeComparisonDialog
          open={mergeDialog}
          onOpenChange={setMergeDialog}
          mergeType={mergeType}
          comparisonData={comparisonData}
          selectedTarget={selectedTarget}
          setSelectedTarget={setSelectedTarget}
          fieldSelections={fieldSelections}
          setFieldSelections={setFieldSelections}
          onSelectAll={handleSelectAll}
          onMerge={handleMerge}
          merging={merging}
        />

        <AddParentDialog
          open={addParentDialog}
          onOpenChange={setAddParentDialog}
          selectedPlayer={selectedPlayer}
          parentSearch={parentSearch}
          setParentSearch={setParentSearch}
          allParents={allParents}
          onAdd={handleAddParent}
        />
      </div>
    </DashboardLayout>
  );
};

export default DuplicateManagement;
